"use client";

import { motion } from "framer-motion";
import {
  CheckCircle2,
  Circle,
  FileText,
  Key,
  Lightbulb,
  Plus,
  Shield,
  Upload,
} from "lucide-react";
import { useEffect, useState } from "react";

import { DocumentPreviewModal } from "@/components/app/documents/document-preview-modal";
import { DocumentSelectorDialog } from "@/components/app/documents/document-selector-dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useJourneyData } from "@/hooks/use-journey-data";
import { downloadFile } from "@/lib/download-file";
import { uploadFile } from "@/lib/storage";
import type { Document } from "@/schemas/document";
import { CelebrationModal } from "./celebration-modal";

const INSURANCE_DOC_REF = "insurance_policy";
const TASK_REFS = {
  insurance: "confirm_insurance_active",
  utilities: "schedule_utility_transfers",
  keys: "get_keys",
};

const TOTAL_ITEMS = 3; // 1 document + 2 tasks

export function ClosingChecklist() {
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  const {
    createTask,
    documents,
    journeyDocuments,
    journeyTasks,
    tasks,
    createDocument,
    addBuyerDocument,
    addBuyerTask,
    toggleTaskComplete,
    journeyData,
    userProfile,
  } = useJourneyData();

  // Get insurance document
  const insuranceDoc = documents.find((d) =>
    journeyDocuments.some(
      (bd) => bd.document_id === d.id && bd.reference_id === INSURANCE_DOC_REF,
    ),
  );

  // Get tasks
  const getTask = (ref: string) =>
    tasks.find((t) =>
      journeyTasks.some((bt) => bt.task_id === t.id && bt.reference_id === ref),
    );

  const insuranceTask = getTask(TASK_REFS.insurance);
  const utilitiesTask = getTask(TASK_REFS.utilities);
  const keysTask = getTask(TASK_REFS.keys);

  const handleUpload = async (data: { file: File }) => {
    if (!journeyData?.id) return;

    try {
      const fileName = `${userProfile?.id}/documents/${Date.now()}_${data.file.name}`;
      const { url, path } = await uploadFile("documents", fileName, data.file);

      // First create the document
      const newDoc = await createDocument.mutateAsync({
        type: "insurance",
        category: "insurance",
        name: data.file.name,
        url,
        file_path: path,
        file_type: data.file.type,
        size_bytes: data.file.size,
      });

      // Then create the buyer-document relationship
      await addBuyerDocument.mutateAsync({
        document_id: newDoc.id,
        reference_id: INSURANCE_DOC_REF,
        journey_id: journeyData.id,
        user_id: userProfile?.id ?? "",
      });
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    }
  };

  const getTaskForRef = (ref: string) =>
    tasks.find((t) =>
      journeyTasks.some((bt) => bt.task_id === t.id && bt.reference_id === ref),
    );

  const handleTaskToggle = async (ref: string) => {
    const existingJourneyTask = journeyTasks.find(
      (bt) => bt.reference_id === ref,
    );
    const existingTask = existingJourneyTask
      ? tasks.find((t) => t.id === existingJourneyTask.task_id)
      : null;

    if (!existingTask) {
      try {
        if (!journeyData?.id) throw new Error("No journey ID found");

        // Create the task with proper title based on ref
        const taskTitle = {
          [TASK_REFS.insurance]: "Confirm Insurance Active",
          [TASK_REFS.utilities]: "Transfer Utilities",
          [TASK_REFS.keys]: "Get Keys",
        }[ref];

        const newTask = await createTask.mutateAsync({
          title: taskTitle,
          category: "other",
          priority: "high",
          due_date: null,
          reference_id: ref,
        });

        // Create the buyer-task relationship
        await addBuyerTask.mutateAsync({
          task_id: newTask.id,
          reference_id: ref,
          journey_id: journeyData.id,
          user_id: userProfile?.id ?? "",
        });
      } catch (error) {
        console.error("Failed to create task:", error);
      }
    } else {
      await toggleTaskComplete.mutateAsync({
        id: existingTask.id,
        completed: !existingTask.completed,
      });
    }
  };

  // Add this helper function for progress calculation
  const calculateProgress = () => {
    const documentCount = insuranceDoc ? 1 : 0;
    const taskCount = [utilitiesTask, keysTask].filter(
      (t) => t?.completed,
    ).length;
    return ((documentCount + taskCount) / TOTAL_ITEMS) * 100;
  };

  const progress = calculateProgress();
  const completedCount = Math.round((progress / 100) * TOTAL_ITEMS);

  useEffect(() => {
    if (progress === 100 && !showCelebration) {
      setShowCelebration(true);
    }
  }, [progress]);

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold">Closing Day Checklist</h3>
          <p className="text-sm text-muted-foreground">
            Complete these final steps to begin your homeownership journey
          </p>
        </div>
        <div className="md:text-right w-full md:w-auto">
          <p className="text-center md:text-right text-sm font-semibold">
            {completedCount} of {TOTAL_ITEMS} complete
          </p>
          <Progress value={progress} className="md:w-32 mt-1" />
        </div>
      </div>

      <div className="space-y-4">
        {/* Insurance Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">Insurance Policy</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload your homeowner's insurance policy
                  </p>
                </div>
              </div>

              <div className="ml-auto">
                {!insuranceDoc ? (
                  <Button
                    variant="outline"
                    onClick={() => setIsDocumentDialogOpen(true)}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewDocument(insuranceDoc)}
                  >
                    View Policy
                  </Button>
                )}
              </div>
            </div>

            {insuranceDoc && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-4 pt-4 border-t"
              >
                <div className="flex items-center gap-4">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">
                      {`${insuranceDoc.name.slice(0, 50)}${
                        insuranceDoc.name.length > 50 ? "..." : ""
                      }`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Added{" "}
                      {new Date(insuranceDoc.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {canPreviewFile(insuranceDoc.file_type) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewDocument(insuranceDoc)}
                      >
                        Preview
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        downloadFile(insuranceDoc.url, insuranceDoc.name)
                      }
                    >
                      Download
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Utilities Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Lightbulb className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">Transfer Utilities</h4>
                  <p className="text-sm text-muted-foreground">
                    Schedule utility transfers for closing day
                  </p>
                </div>
              </div>

              {(() => {
                const task = getTaskForRef(TASK_REFS.utilities);
                const isCompleted = task?.completed;
                const isTracking = !!task;

                return (
                  <div className="ml-auto">
                    {!isTracking ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTaskToggle(TASK_REFS.utilities)}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Track Task
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTaskToggle(TASK_REFS.utilities)}
                        className="gap-2"
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground" />
                        )}
                        {isCompleted ? "Completed" : "Mark Complete"}
                      </Button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </motion.div>

        {/* Keys Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
            <div className="flex flex-col md:flex-row items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Key className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">Get Keys</h4>
                  <p className="text-sm text-muted-foreground">
                    Receive and verify all keys and access devices
                  </p>
                </div>
              </div>

              {(() => {
                const task = getTaskForRef(TASK_REFS.keys);
                const isCompleted = task?.completed;
                const isTracking = !!task;

                return (
                  <div className="ml-auto">
                    {!isTracking ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTaskToggle(TASK_REFS.keys)}
                        className="gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Track Task
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTaskToggle(TASK_REFS.keys)}
                        className="gap-2"
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground" />
                        )}
                        {isCompleted ? "Completed" : "Mark Complete"}
                      </Button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </motion.div>
      </div>

      {completedCount === TOTAL_ITEMS && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-6 pt-6 border-t text-center"
        >
          <h4 className="font-semibold text-primary mb-2">
            🎉 Congratulations!
          </h4>
          <p className="text-sm text-muted-foreground">
            You've completed all closing tasks. Welcome to your new home!
          </p>
        </motion.div>
      )}

      <DocumentSelectorDialog
        open={isDocumentDialogOpen}
        onOpenChange={setIsDocumentDialogOpen}
        title="Upload Insurance Policy"
        description="Upload your homeowner's insurance policy document"
        documents={documents}
        selectedDocument={insuranceDoc ?? null}
        onSelect={(doc) => {
          if (doc && journeyData?.id) {
            addBuyerDocument.mutate({
              document_id: doc.id,
              reference_id: INSURANCE_DOC_REF,
              journey_id: journeyData.id,
              user_id: userProfile?.id ?? "",
            });
          }
          setIsDocumentDialogOpen(false);
        }}
        onUpload={handleUpload}
        category="insurance"
        type="insurance"
      />

      <DocumentPreviewModal
        document={previewDocument}
        onOpenChange={(open) => !open && setPreviewDocument(null)}
      />

      <CelebrationModal
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        buyerName={userProfile?.first_name || undefined}
      />
    </div>
  );
}

// Helper function for file preview
const canPreviewFile = (fileType: string) => {
  return fileType.startsWith("image/") || fileType === "application/pdf";
};
